import { setSentryUser } from '@paragon/client-tools';
import { StripePriceLookupKey, User } from '@paragon/contract-library';
import { userService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';

import * as authenticationConnection from '../connections/authenticationConnection';
import { cookiesFetchToken, cookiesRemoveToken, cookiesStoreToken } from '../cookies';
import { getLoggedInUser } from '../selectors/users';
import {
  automaticLogInAttemptComplete,
  automaticLogInAttemptStarted,
  logIn,
  passwordChangedInResponseToServerRequest,
  passwordChangeRequestedByServer,
  setSubscription,
} from '../store/logInDataSlice';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionReturn } from '../thunkActions';

function hasOngoingFreeTrial(user: User | null, stripeSubscription: 'none' | StripePriceLookupKey): boolean {
  return stripeSubscription === 'none' && user?.trialEndDate != null && new Date(user.trialEndDate) > new Date();
}

const finalizeLogin =
  (token: string, user: User, transientSession?: boolean): ThunkActionReturn<User> =>
  (dispatch) => {
    if (!transientSession) {
      dispatch(cookiesStoreToken(token));
    }

    user.token = token;
    dispatch(logIn({ user }));

    if (user.needsPasswordChange) {
      dispatch(passwordChangeRequestedByServer());
    }

    setSentryUser(user);

    if (!transientSession) {
      userService.organizations
        .getStripeSubscription(user.organization)
        .then(({ name, cancelAtPeriodEnd, currentPeriodEnd }) => {
          let subscription: 'none' | 'free' | 'trial' | StripePriceLookupKey = name;
          if (hasOngoingFreeTrial(user, name)) {
            subscription = 'trial';
          } else if (user.roleSet.role === 'Free') {
            subscription = 'free';
          }
          dispatch(
            setSubscription({
              subscription,
              cancelAtPeriodEnd,
              currentPeriodEnd: currentPeriodEnd ? new Date(currentPeriodEnd).toISOString() : undefined,
            }),
          );
        })
        .catch((error) => {
          console.error('Failed to fetch subscription after login:', error);
          dispatch(enqueueError('Failed to fetch subscription information.'));
        });
    }

    return user;
  };

/**
 * If you are calling this function as part of an automatic log-in attempt, make sure it is wrapped with calls to
 * `automaticLogInAttemptStarted` and `automaticLogInAttemptComplete`.
 */
export const logInWithCredentials =
  (email: string, password: string): AsyncThunkActionReturn<User | null> =>
  async (dispatch) => {
    try {
      const result = await authenticationConnection.pAuthenticateUser(email, password);
      return dispatch(finalizeLogin(result.token, result.user));
    } catch {
      return null;
    }
  };

/**
 * If you are calling this function as part of an automatic log-in attempt, make sure it is wrapped with calls to
 * `automaticLogInAttemptStarted` and `automaticLogInAttemptComplete`.
 */
export const logInWithGuids =
  (organizationGuid: string, customerGuid: string): AsyncThunkActionReturn<User | null> =>
  async (dispatch) => {
    try {
      const result = await authenticationConnection.pValidateGuids(organizationGuid, customerGuid);
      if (!result.token) {
        return null;
      }

      return dispatch(finalizeLogin(result.token, result.user, true));
    } catch {
      return null;
    }
  };

/**
 * If you are calling this function as part of an automatic log-in attempt, make sure it is wrapped with calls to
 * `automaticLogInAttemptStarted` and `automaticLogInAttemptComplete`.
 */
export const logInWithTransientToken =
  (token: string): AsyncThunkActionReturn<User | null> =>
  async (dispatch) => {
    try {
      return dispatch(finalizeLogin(token, await authenticationConnection.pValidateToken(token), true));
    } catch {
      return null;
    }
  };

/**
 * If you are calling this function as part of an automatic log-in attempt, make sure it is wrapped with calls to
 * `automaticLogInAttemptStarted` and `automaticLogInAttemptComplete`.
 */
export const logInWithStoredToken = (): AsyncThunkActionReturn<User | null> => async (dispatch) => {
  const token = dispatch(cookiesFetchToken());
  if (typeof token !== 'string') {
    return null;
  }

  try {
    return dispatch(finalizeLogin(token, await authenticationConnection.pValidateToken(token)));
  } catch {
    return null;
  }
};

/**
 * The default way that most clients should attempt to automatically log the user in. This should be called (but does
 * not need to be awaited) before the React tree is rendered to avoid any potential flash of the log-in screen.
 */
export const attemptAutomaticLogInWithStoredToken = (): AsyncThunkActionReturn<User | null> => async (dispatch) => {
  dispatch(automaticLogInAttemptStarted());

  try {
    return await dispatch(logInWithStoredToken());
  } finally {
    dispatch(automaticLogInAttemptComplete());
  }
};

export const changePassword =
  (password: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const user = getLoggedInUser(state);

    if (!user) {
      throw new Error('Unable to change password: user is not logged in.');
    }

    await userService.users.updatePassword(user.email, password);

    if (state.logInData.needsPasswordChange) {
      dispatch(passwordChangedInResponseToServerRequest());
    }
  };

export const logOut = () => cookiesRemoveToken({ reload: true });
